import React from 'react';
import { every, isEmpty, isNil, map } from 'lodash';
import classNames from 'classnames';

// local dependencies
import { Icon } from 'modules/icon-bank';
import { getLabel } from 'utils/getLabel';
import { memberDetailsHeaderFamilyUnit as MemberDetailsHeaderFamilyUnitView } from 'ui/views/widgets/memberDetailsHeaderFamilyUnit';
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { radioBox as RadioBox } from 'ui/views/generic/radioBox';
import { heartBeatPreloader as HeartBeatPreloader } from 'ui/views/generic/heartBeatPreloader';
import { ACTIONS, ALERTS_LISTING_ACTIONS } from 'constants/common';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { memberAlertsListing as MemberAlertsListing } from 'ui/views/widgets/memberAlertsListing';
 *
 * // render
 * <MemberAlertsListing propName='propValue'/>
 */
const _memberAlertsListing = ( props ) => {
    const { memberId, filteredAlerts, isAccordianOpen, onActionClick, componentData } = props;
    const { navActions, filters, alertTypes, alertActions, noDataToDisplay, state } = componentData;
    const { members, selectedFilter } = state;

    return (
        <div className='ui-member-alerts-listing'>

            {/* nav action */}
            <div className='ui-member-alerts-listing__nav-actions'>
                {
                    map( navActions, navAction => {
                        return (
                            <div
                                key={ navAction.name }
                                className='ui-member-alerts-listing__nav-actions__action'
                                onClick={ () => onActionClick( { type: ALERTS_LISTING_ACTIONS.NAV_ACTION, value: navAction } ) }
                            >
                                <Icon id={ ACTIONS.BACK === navAction.name ? 'backArrow': null } />
                                <div className='ui-member-alerts-listing__nav-actions__action__label'>
                                    { getLabel( navAction.title ) }
                                </div>
                            </div>
                        );
                    } )
                }
            </div>

            {/* member unit */}
            <div className='ui-member-alerts-listing__member-unit'>
                <MemberDetailsHeaderFamilyUnitView
                    memberId={ memberId }
                    members={ members }
                />
            </div>

            {/* main section */}
            <div className='ui-member-alerts-listing__main'>

                {/* filters */}
                <div className='ui-member-alerts-listing__main__filters'>
                    {
                        map( filters, filter => (
                            <div
                                key={ filter.name }
                                className='ui-member-alerts-listing__main__filters__filter'
                                onClick={ () => onActionClick( { type: ALERTS_LISTING_ACTIONS.FILTER_ACTION, value: filter } ) }
                            >
                                <RadioBox
                                    name={ filter.name }
                                    value={ filter.name }
                                    selected={ filter.name === selectedFilter }
                                    className='ui-member-alerts-listing__main__filters__filter__checkbox'
                                />
                                <div className='ui-member-alerts-listing__main__filters__filter__title'>
                                    { getLabel( filter.title ) }
                                </div>
                            </div>
                            )
                        )
                    }
                </div>

                {/* alert types */}
                {
                    every( filteredAlerts, alertType => isEmpty( alertType ) ) ? (
                        <div className='ui-member-alerts-listing__main__alerts--no-data'>
                            { getLabel( noDataToDisplay ) }
                        </div>
                    ) : (
                        <div className='ui-member-alerts-listing__main__alerts'>
                            {
                                map( alertTypes, alertType => {
                                    if ( isEmpty( filteredAlerts[ alertType.name ] ) ) { return null; }

                                    const listingClassName = classNames( 'ui-member-alerts-listing__main__alerts__accordian__listing', {
                                        'ui-member-alerts-listing__main__alerts__accordian__listing--hide': ! isAccordianOpen[ alertType.name ]
                                    } );

                                    return (
                                        <div key={ alertType.name } className='ui-member-alerts-listing__main__alerts__accordian'>

                                            {/* accordian title bar */}
                                            <div
                                                className='ui-member-alerts-listing__main__alerts__accordian__title-bar'
                                                onClick={ () => onActionClick( { type: ALERTS_LISTING_ACTIONS.ACCORDIAN_ACTION, value: alertType } ) }
                                            >
                                                <div className='ui-member-alerts-listing__main__alerts__accordian__title-bar__icon'>
                                                    <Icon id={ isAccordianOpen[ alertType.name ] ? 'accordianDownArrow' : 'accordianRightArrow' } />
                                                </div>
                                                <div className='ui-member-alerts-listing__main__alerts__accordian__title-bar__title'>
                                                    { getLabel( alertType.title ) }
                                                </div>
                                            </div>

                                            {/* alerts listing */}
                                            <div className={ listingClassName }>
                                                {
                                                    map( filteredAlerts[ alertType.name ], alert => (
                                                        <div key={ alert.id } className='ui-member-alerts-listing__main__alerts__accordian__listing__alert'>
                                                            <div className='ui-member-alerts-listing__main__alerts__accordian__listing__alert__title'>
                                                                { alert.title }
                                                            </div>
                                                            <div className='ui-member-alerts-listing__main__alerts__accordian__listing__alert__description'>
                                                                <div
                                                                    className='ui-member-alerts-listing__main__alerts__accordian__listing__alert__description__text'
                                                                    dangerouslySetInnerHTML={ { __html: alert.description } }
                                                                    onClick={ ( event ) => {
                                                                        const actionName = event.target.dataset.action;

                                                                        if ( ! isNil( actionName ) ) {
                                                                            onActionClick( {
                                                                                type: ALERTS_LISTING_ACTIONS.ALERT_TEXT_ACTION,
                                                                                value: actionName,
                                                                                data: alert.actionsConfig[ actionName ]
                                                                            } );
                                                                        }
                                                                    } }
                                                                ></div>
                                                                <div className='ui-member-alerts-listing__main__alerts__accordian__listing__alert__description__actions'>
                                                                    {
                                                                        map( alertActions, action => {
                                                                            return alert.actionsVisibility[ action.visibility ] ? (
                                                                                <div
                                                                                    key={ action.name }
                                                                                    className='ui-member-alerts-listing__main__alerts__accordian__listing__alert__description__actions__action'
                                                                                    onClick={ () => onActionClick( { type: ALERTS_LISTING_ACTIONS.ALERT_ACTION, value: action, data: alert } ) }
                                                                                >
                                                                                    <div className='ui-member-alerts-listing__main__alerts__accordian__listing__alert__description__actions__action__icon'>
                                                                                        <Icon id={ action.name } />
                                                                                    </div>
                                                                                    <div className='ui-member-alerts-listing__main__alerts__accordian__listing__alert__description__actions__action__title'>
                                                                                        { getLabel( action.title ) }
                                                                                    </div>
                                                                                </div>
                                                                            ) : null;
                                                                        } )
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) )
                                                }
                                            </div>
                                        </div>
                                    );
                                } )
                            }
                        </div>
                        
                    )
                }
            </div>
        </div>
    );
};

// set display name
_memberAlertsListing.displayName = 'memberAlertsListing';

// set default props
_memberAlertsListing.defaultProps = {};

// prop types
_memberAlertsListing.propTypes = {};


export const memberAlertsListing = withNetworkState( HeartBeatPreloader, _memberAlertsListing );
